import { useState, useCallback } from 'react';
import proj4 from 'proj4';

// Define the EPSG:3908, EPSG8678 projection
proj4.defs("EPSG:3908", "+proj=tmerc +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=682,-203,480,0,0,0,0 +units=m +no_defs +type=crs");
proj4.defs("EPSG:8678", "+proj=tmerc +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=682,-203,480,0,0,0,0 +units=m +no_defs +type=crs");

const extractLayerNameFromUrl = (url, setTypeNameFromUrl) => {
  try {
    const urlObj = new URL(url);
    const layerParam = urlObj.searchParams.get('typename') || urlObj.searchParams.get('typeName');
    setTypeNameFromUrl(layerParam);
    return layerParam ? layerParam.split(':').pop() : 'unknown_layer';
  } catch (error) {
    console.error('Invalid URL:', error);
    return 'unknown_layer';
  }
};

const transformCoordinates = (coordinates, fromCRS, toCRS) => {
  if (fromCRS === toCRS) {
    return coordinates;
  }
  if (Array.isArray(coordinates[0])) {
    return coordinates.map(coord => transformCoordinates(coord, fromCRS, toCRS));
  }
  return proj4(fromCRS, toCRS, coordinates);
};

export const useImportLayer = () => {
  const [data, setData] = useState(null);
  const [layerCRS, setLayerCRS] = useState("EPSG:4326");
  const [geometryType, setGeometryType] = useState(null);
  const [layerName, setLayerName] = useState(null);
  const [featureProperties, setFeatureProperties] = useState({});
  const [geometryName, setGeometryName] = useState("the_geom");
  
 const [typeNameFromUrl, setTypeNameFromUrl] = useState("");

  const fetchData = useCallback(async (fetchUrl) => {
    try {
      const response = await fetch(fetchUrl, { mode: 'cors' });
      if (!response.ok) throw new Error('Network response was not ok');
      
      const geoJsonData = await response.json();

      if (!geoJsonData.features || geoJsonData.features.length === 0) {
        throw new Error('No features found in the GeoJSON data.');
      }

      let crs = geoJsonData.crs && geoJsonData.crs.properties && geoJsonData.crs.properties.name
        ? geoJsonData.crs.properties.name.replace("urn:ogc:def:crs:", "").replace("::", ":")
        : "EPSG:4326";
      setLayerCRS(crs);

      const parsedLayerName = extractLayerNameFromUrl(fetchUrl, setTypeNameFromUrl);
      setLayerName(parsedLayerName);

      if (!proj4.defs[crs]) {
        console.warn(`CRS ${crs} is not defined in proj4. Using EPSG:4326 as default.`);
        crs = "EPSG:4326";
      }

      const transformedGeoJsonData = {
        ...geoJsonData,
        features: geoJsonData.features
          .filter(feature => feature.geometry && feature.geometry.coordinates)
          .map((feature) => {
            if (feature.geometry && feature.geometry.coordinates) {
              feature.geometry.coordinates = transformCoordinates(feature.geometry.coordinates, crs, "EPSG:4326");
            }
            return feature;
          })
      };

      setData(transformedGeoJsonData);
      setGeometryType(transformedGeoJsonData.features[0]?.geometry?.type);

      if (transformedGeoJsonData.features[0]) {
        setFeatureProperties(transformedGeoJsonData.features[0].properties);
        setGeometryName(transformedGeoJsonData.features[0].geometry_name || "the_geom");
      }

      return transformedGeoJsonData;

    } catch (error) {
      console.error('Error loading file:', error);
      return null;
    }
  }, []);
  return { fetchData, typeNameFromUrl };
};